import { Link } from "react-router-dom";
import {
    Leaf,
    Check,
    ClipboardCheck,
    Thermometer,
    Receipt,
    CalendarDays,
    FileText,
    Smartphone,
    Sparkles,
    ChevronRight,
    Shield,
    Cloud,
    Zap,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useT } from "../lib/i18n";

export default function Landing() {
    const { t } = useT();

    const features = [
        { icon: ClipboardCheck, title: t("landing.feature_haccp_title"), desc: t("landing.feature_haccp_desc") },
        { icon: Thermometer, title: t("landing.feature_temp_title"), desc: t("landing.feature_temp_desc") },
        { icon: Receipt, title: t("landing.feature_ocr_title"), desc: t("landing.feature_ocr_desc") },
        { icon: CalendarDays, title: t("landing.feature_deadlines_title"), desc: t("landing.feature_deadlines_desc") },
        { icon: FileText, title: t("landing.feature_pdf_title"), desc: t("landing.feature_pdf_desc") },
        { icon: Smartphone, title: t("landing.feature_pwa_title"), desc: t("landing.feature_pwa_desc") },
    ];
    const targets = [
        t("landing.target_ortofrutta"),
        t("landing.target_alimentari"),
        t("landing.target_macelleria"),
        t("landing.target_panificio"),
        t("landing.target_ristorante"),
        t("landing.target_gastronomia"),
    ];
    const benefits = [
        { icon: Zap, title: t("landing.benefit_time_title"), desc: t("landing.benefit_time_desc") },
        { icon: Shield, title: t("landing.benefit_legal_title"), desc: t("landing.benefit_legal_desc") },
        { icon: Cloud, title: t("landing.benefit_cloud_title"), desc: t("landing.benefit_cloud_desc") },
    ];

    return (
        <div className="min-h-screen bg-bone" data-testid="landing-page">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-bone/95 backdrop-blur border-b border-soft">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-forest text-white grid place-items-center shadow-sm">
                            <Leaf size={20} />
                        </div>
                        <div className="hidden sm:block">
                            <div className="font-display text-lg leading-tight text-forest-dark">HACCP Manager</div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 -mt-0.5">{t("brand.tagline")}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSwitcher compact />
                        <Link
                            to="/scarica"
                            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-lg bg-stone-900 text-white hover:bg-stone-800 transition-all whitespace-nowrap"
                            data-testid="landing-header-download"
                        >
                            <Smartphone size={14} /> Scarica app
                        </Link>
                        <Link to="/login" className="btn-ghost text-sm" data-testid="landing-login">{t("landing.header_login")}</Link>
                        <Link to="/register" className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="landing-register">
                            {t("landing.header_register")} <ChevronRight size={14} />
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-warm/15 text-terracotta rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                            <Sparkles size={14} /> {t("landing.cta_trial_badge")}
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest-dark leading-[1.05] mb-5">
                            {t("landing.hero_title_1")} <span className="text-accent-warm">{t("landing.hero_title_2")}</span>
                        </h1>
                        <p className="text-lg text-stone-600 mb-7 leading-relaxed">
                            {t("landing.hero_subtitle")}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/register"
                                className="btn-primary inline-flex items-center justify-center gap-2 text-base px-7 py-3.5"
                                data-testid="hero-register"
                            >
                                <Sparkles size={18} /> {t("landing.cta_start_free")}
                            </Link>
                            <Link
                                to="/scarica"
                                className="inline-flex items-center justify-center gap-2 text-base px-7 py-3.5 rounded-xl font-semibold bg-stone-900 text-white border border-stone-900 hover:bg-stone-800 transition-all whitespace-nowrap"
                                data-testid="hero-download"
                            >
                                <Smartphone size={18} /> Scarica ora
                            </Link>
                            <Link
                                to="/login"
                                className="btn-ghost inline-flex items-center justify-center gap-2 text-base px-7 py-3.5"
                            >
                                {t("landing.cta_login")}
                            </Link>
                        </div>
                        <p className="text-xs text-stone-500 mt-5">
                            {t("landing.compliance_note")}
                        </p>
                    </div>
                    <div className="relative">
                        <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-forest to-forest-dark p-1.5 shadow-2xl rotate-1">
                            <div className="w-full h-full bg-white rounded-[20px] overflow-hidden">
                                <div className="bg-bone-2 px-4 py-2 flex items-center gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                                    <div className="ml-3 text-[10px] text-stone-500 truncate">haccpmanger.it</div>
                                </div>
                                <div className="p-5 space-y-3">
                                    <div className="flex items-center gap-2">
                                        <ClipboardCheck size={18} className="text-forest" />
                                        <div>
                                            <div className="text-[10px] uppercase font-bold tracking-widest text-stone-500">Controlli HACCP oggi</div>
                                            <div className="font-display text-2xl text-forest-dark">9/13 <span className="text-sm text-stone-400">69%</span></div>
                                        </div>
                                    </div>
                                    <div className="bg-bone-2 rounded-lg p-3">
                                        <div className="flex justify-between items-center mb-1.5">
                                            <span className="text-xs font-semibold text-stone-600">Frigo bar — 4°C</span>
                                            <span className="text-xs text-forest font-bold">OK</span>
                                        </div>
                                        <div className="h-2 bg-forest-50 rounded-full overflow-hidden">
                                            <div className="h-full w-3/4 bg-forest rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="bg-bone-2 rounded-lg p-3">
                                        <div className="text-xs text-stone-500">Fattura recente</div>
                                        <div className="font-medium text-stone-700">Ortomercato Genova</div>
                                        <div className="text-forest font-bold text-sm">353,94 €</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="bg-white border-y border-soft py-14">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-8">
                    {benefits.map(({ icon: Icon, title, desc }) => (
                        <div key={title} className="text-center">
                            <div className="w-14 h-14 rounded-2xl bg-forest-50 text-forest grid place-items-center mx-auto mb-4">
                                <Icon size={26} />
                            </div>
                            <h3 className="font-display text-lg text-forest-dark mb-1">{title}</h3>
                            <p className="text-sm text-stone-600">{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                <div className="text-center mb-12">
                    <h2 className="font-display text-3xl sm:text-4xl text-forest-dark mb-3">Tutto quello che ti serve</h2>
                    <p className="text-stone-600 max-w-2xl mx-auto">
                        Un'unica app per HACCP, fatture, scadenze, documenti e report. Niente più Excel, raccoglitori,
                        post-it sui frigoriferi.
                    </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map(({ icon: Icon, title, desc }) => (
                        <div key={title} className="card-soft p-6 hover:shadow-md transition-shadow">
                            <div className="w-11 h-11 rounded-xl bg-forest-50 text-forest grid place-items-center mb-3">
                                <Icon size={22} />
                            </div>
                            <h3 className="font-display text-lg text-forest-dark mb-1.5">{title}</h3>
                            <p className="text-sm text-stone-600 leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Targets */}
            <section className="bg-forest text-bone py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="font-display text-3xl sm:text-4xl text-center mb-3">Pensata per chi vende cibo</h2>
                    <p className="text-bone-2/80 text-center max-w-2xl mx-auto mb-10">
                        Tutte le attività soggette al manuale HACCP possono usare HACCP Manager.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
                        {targets.map((t) => (
                            <div key={t} className="flex items-center gap-2.5 bg-forest-dark/40 rounded-xl px-4 py-3">
                                <Check size={18} className="text-accent-warm shrink-0" />
                                <span className="text-sm font-medium">{t}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                <div className="text-center mb-10">
                    <h2 className="font-display text-3xl sm:text-4xl text-forest-dark mb-3">Un prezzo. Tutto incluso.</h2>
                    <p className="text-stone-600">Nessun extra, nessuna sorpresa, disdici quando vuoi.</p>
                </div>
                <div className="max-w-md mx-auto card-soft p-8 border-2 border-forest">
                    <div className="text-center mb-6">
                        <div className="text-sm font-bold uppercase tracking-widest text-accent-warm mb-2">Piano Mensile</div>
                        <div className="flex items-baseline justify-center gap-1">
                            <span className="font-display text-5xl text-forest-dark">9,99</span>
                            <span className="text-stone-500 text-lg">€/mese</span>
                        </div>
                        <p className="text-sm text-stone-500 mt-2">7 giorni di prova gratuita</p>
                    </div>
                    <ul className="space-y-2.5 mb-7">
                        {[
                            "Registro HACCP digitale completo",
                            "Frigoriferi e temperature illimitati",
                            "AI OCR fatture illimitato",
                            "Scadenze, documenti, calendario",
                            "Report PDF per ASL e NAS",
                            "Notifiche push (anche offline)",
                            "Aggiornamenti automatici",
                            "Supporto via email",
                        ].map((f) => (
                            <li key={f} className="flex items-start gap-2 text-sm text-stone-700">
                                <Check size={16} className="text-forest mt-0.5 shrink-0" />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>
                    <Link
                        to="/register"
                        className="btn-primary w-full inline-flex items-center justify-center gap-2 text-base py-3"
                        data-testid="pricing-register"
                    >
                        <Sparkles size={18} /> Inizia prova gratuita
                    </Link>
                    <p className="text-[11px] text-stone-500 text-center mt-3">
                        Nessuna carta richiesta · Pagamento sicuro con Stripe
                    </p>
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-bone-2 py-16 border-t border-soft">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="font-display text-3xl text-forest-dark mb-3">Pronto a digitalizzare il tuo negozio?</h2>
                    <p className="text-stone-600 mb-6">
                        Inizia oggi. 7 giorni gratis, senza carta. Saranno minuti ben spesi.
                    </p>
                    <Link
                        to="/register"
                        className="btn-primary inline-flex items-center gap-2 px-8 py-3.5 text-base"
                        data-testid="cta-register"
                    >
                        Crea account gratis <ChevronRight size={18} />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-soft py-8 text-center text-xs text-stone-500">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <p>© 2026 HACCP Manager · haccpmanger.it · Reg. CE 852/2004</p>
                    <p className="mt-1">
                        <Link to="/login" className="hover:text-forest">Accedi</Link>
                        {" · "}
                        <Link to="/scarica" className="hover:text-forest" data-testid="footer-scarica">Scarica l&apos;app</Link>
                        {" · "}
                        <Link to="/privacy" className="hover:text-forest" data-testid="footer-privacy">Privacy Policy</Link>
                        {" · "}
                        <Link to="/termini" className="hover:text-forest" data-testid="footer-termini">Termini</Link>
                    </p>
                </div>
            </footer>
        </div>
    );
}
